import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { useToggle } from 'react-use';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import { Col, Row } from 'react-bootstrap';
import { filterOptions } from 'hooks/useNotification';
import {
  useHubChromeEnabled,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';
import NewNotificationDrawer from './NotificationsTab/NewNotificationDrawer';
import NotificationsList from './NotificationsTab/NotificationsList';
import { NotificationAutomationSettingsPage } from './NotificationsTab/NotificationAutomationSettingsPage';
import { useCreateIntent } from '../useCreateIntent';
import { registerFocNotificationsTourApi } from '../focNotificationsTour/focNotificationsTourBridge';
import { FOC_NOTIFICATIONS_TOUR_PARAM } from '../focNotificationsTour/focNotificationsTourSteps';

type NotificationsFilter = 'active' | 'historical' | 'sent';

const Notifications: React.FC = () => {
  const hubChrome = useHubChromeEnabled();
  const focRequirements = useUscgFocRequirementsFlag();
  const [addOpen, toggleAddDrawer] = useToggle(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsOpen = searchParams.get('notificationSettings') === '1';
  const [selectedFilterOption, setSelectedFilterOption] =
    useState<NotificationsFilter>('active');
  const tourStartedRef = useRef(false);

  useCreateIntent(
    'notification',
    useCallback(() => toggleAddDrawer(true), [toggleAddDrawer])
  );

  const handleSettingsOpenChange = useCallback(
    (open: boolean) => {
      setSearchParams(
        (params) => {
          if (open) {
            params.set('notificationSettings', '1');
          } else {
            params.delete('notificationSettings');
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    return registerFocNotificationsTourApi({
      openCompose: () => toggleAddDrawer(true),
      closeCompose: () => toggleAddDrawer(false),
      setFilter: (filter) => setSelectedFilterOption(filter),
      openAutomationSettings: () => handleSettingsOpenChange(true),
      closeAutomationSettings: () => handleSettingsOpenChange(false),
    });
  }, [handleSettingsOpenChange, toggleAddDrawer]);

  const wantsTour = searchParams.get(FOC_NOTIFICATIONS_TOUR_PARAM) === '1';

  useEffect(() => {
    if (!wantsTour) {
      tourStartedRef.current = false;
      return;
    }
    if (!focRequirements || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_NOTIFICATIONS_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../focNotificationsTour/focNotificationsTour')
        .then(({ startFocNotificationsTour }) => {
          startFocNotificationsTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [focRequirements, setSearchParams, wantsTour]);

  if (settingsOpen) {
    return (
      <div data-tour="notifications-automation">
        <NotificationAutomationSettingsPage
          onBack={() => handleSettingsOpenChange(false)}
        />
      </div>
    );
  }

  if (hubChrome) {
    return (
      <div
        className="dark tw:space-y-3 tw:bg-background tw:p-1 tw:text-foreground"
        data-tour="notifications-workspace"
      >
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <h2 className="tw:m-0 tw:text-lg tw:font-semibold tw:text-foreground">
            Notifications
          </h2>
          <div className="tw:flex tw:items-center tw:gap-2">
            <Button
              type="button"
              size="sm"
              data-tour="notifications-compose"
              onClick={() => toggleAddDrawer(true)}
            >
              + Create Notification
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label="Notifications options"
                    data-tour="notifications-filter"
                  >
                    <MoreVertical className="tw:h-4 tw:w-4" />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="dark tw:w-52">
                {filterOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt}
                    className={cn(
                      'tw:capitalize',
                      selectedFilterOption === opt && 'tw:bg-accent'
                    )}
                    onClick={() =>
                      setSelectedFilterOption(opt as NotificationsFilter)
                    }
                  >
                    {opt}
                    {selectedFilterOption === opt ? ' ✓' : ''}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleSettingsOpenChange(true)}
                >
                  Notification Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div data-tour="notifications-active-list">
          <NotificationsList filter={selectedFilterOption} />
        </div>
        {addOpen ? <NewNotificationDrawer toggle={toggleAddDrawer} /> : null}
      </div>
    );
  }

  return (
    <div className="p-2" data-tour="notifications-workspace">
      <Row className="align-items-center justify-content-between mb-2">
        <Col>
          <div>Notifications</div>
        </Col>
        <Col xs="auto">
          <StylishNewButton
            data-tour="notifications-compose"
            onClick={toggleAddDrawer}
          >
            Add Notification
          </StylishNewButton>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col xs="auto">
          <div data-tour="notifications-filter">
            <StylishNewToggleButtonGroup
              className="justify-content-start"
              size="sml"
              data={filterOptions.map((opt) => ({
                label: opt.toUpperCase(),
                value: opt,
              }))}
              selected={selectedFilterOption}
              onChange={(value) =>
                setSelectedFilterOption(value as NotificationsFilter)
              }
              onBlur={() => {}}
            />
          </div>
        </Col>
      </Row>
      <div data-tour="notifications-active-list">
        <NotificationsList filter={selectedFilterOption} />
      </div>

      {addOpen ? <NewNotificationDrawer toggle={toggleAddDrawer} /> : null}
    </div>
  );
};

export default Notifications;
